import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';
import format from 'date-fns/format';
import { push } from 'connected-react-router';
import { Logger } from 'utils/Logger';
import {
  requestCaseFailure,
  requestCaseSuccess,
  requestCasesListFailure,
  requestCasesListSuccess,
  requestCancelCaseSuccess,
  requestCancelCaseFailure,
  requestDeleteBusinessProfileSuccess,
  requestDeleteBusinessProfileFailure,
  requestSnapshot,
  requestSnapshotSuccess,
  requestSnapshotFailure,
} from './actions';

import {
  caseDetail,
  casesList,
  cancelCase,
  deleteBusinessProfile,
  SSE_UPDATE_SNAPSHOT_PROFILE_GENERATION_PROGRESS,
} from './types';

const logger = new Logger('Saga:Cases');

export function* requestCasesListSaga() {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(authApiService.get, routerService.generate('api.platform.cases'));

    yield put(requestCasesListSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request cases list: ', error);
    yield put(requestCasesListFailure(error));
  }
}

export function* requestCaseSaga(action) {
  const { id } = action;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.platform.caseById', { id }),
    );

    const activities = yield call(
      authApiService.get,
      routerService.generate('api.platform.caseActivities', { id }),
    );

    yield put(
      requestCaseSuccess({
        ...response.data,
        activities: activities.data,
      }),
    );
  } catch (error) {
    logger.error('Failed to request cases list: ', error);
    yield put(requestCaseFailure(error));
  }
}

export function* requestCancelCaseSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.platform.cancel_case', { id: action.caseId }),
    );
    action.promise && action.promise.resolve(response.data);
    yield put(requestCancelCaseSuccess(action.caseId, response.data));
    yield put(push('/cases'));
  } catch (error) {
    logger.error('Failed to request cancel case with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestCancelCaseFailure(error));
  }
}

export function* requestDeleteBusinessProfileSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.delete,
      routerService.generate('api.platform.entity_all', { entityId: action.businessId }),
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestDeleteBusinessProfileSuccess(action.businessId, response.data));
    yield put(push('/cases'));
  } catch (error) {
    logger.error('Failed to request delete profile with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestDeleteBusinessProfileFailure(error));
  }
}

export function* requestSnapshotUpdateSaga(action) {
  const entityId = parseInt(action?.payload?.entityId, 10);
  const finished = action?.payload?.finished;
  const error = action?.payload?.error;

  if (error) {
    yield put(requestSnapshotFailure(entityId, error));
    logger.error('Failed to generate snapshot', error);

    return;
  }

  yield put(requestSnapshot(entityId));

  if (!finished) {
    return;
  }

  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  const response = yield call(
    authApiService.get,
    routerService.generate('api.monitor.snapshot', { entityId }),
  );

  const connection = response?.data?.integrations?.privateAccounting;

  yield put(
    requestSnapshotSuccess(
      entityId,
      connection,
      format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
    ),
  );
}

export default function* casesSaga() {
  return yield all([
    takeLatest(caseDetail.request, requestCaseSaga),
    takeLatest(casesList.request, requestCasesListSaga),
    takeLatest(cancelCase.request, requestCancelCaseSaga),
    takeLatest(deleteBusinessProfile.request, requestDeleteBusinessProfileSaga),
    takeLatest(SSE_UPDATE_SNAPSHOT_PROFILE_GENERATION_PROGRESS, requestSnapshotUpdateSaga),
  ]);
}
